import { useCallback, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePeople, useJobs, useBatchArchivePeople, useBatchReprocessPeople } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { SearchInput } from '../components/ui/SearchInput';
import { FilterDropdown, type FilterOption } from '../components/ui/FilterDropdown';
import {
  Users,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Star,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Search,
  Archive,
  RotateCcw,
  CheckSquare,
  Square,
  MinusSquare,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { SkeletonCardGrid } from '../components/ui/Skeleton';
import type { Person } from '../types';

const statusOptions: FilterOption[] = [
  { value: 'received', label: 'Received' },
  { value: 'enriching', label: 'Enriching' },
  { value: 'enriched', label: 'Enriched' },
  { value: 'ai_processing', label: 'AI Processing' },
  { value: 'ai_complete', label: 'AI Complete' },
  { value: 'synced', label: 'Synced' },
  { value: 'error', label: 'Error' },
];

function ScoreChip({ score }: { score: number | null }) {
  if (score === null) return <span className="text-xs text-gray-400">No score</span>;

  const score5 = score / 10;
  const pct = score * 2;
  const color =
    pct >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800'
    : pct >= 60 ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800'
    : pct >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800'
    : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800';

  return (
    <div className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 ${color}`}>
      <Star className="h-3 w-3 fill-current" />
      <span className="text-sm font-semibold tabular-nums">{score5.toFixed(1)}</span>
      <span className="text-xs opacity-60">/5</span>
    </div>
  );
}

function PersonCard({
  person,
  isSelected,
  onToggleSelect,
  selectionMode,
}: {
  person: Person;
  isSelected: boolean;
  onToggleSelect: (id: number) => void;
  selectionMode: boolean;
}) {
  const name = `${person.firstName} ${person.lastName}`.trim();

  return (
    <div
      className={`group relative rounded-lg border bg-white dark:bg-gray-800 p-3 sm:p-4 transition-all hover:shadow-md ${
        isSelected
          ? 'border-primary-400 dark:border-primary-600 ring-1 ring-primary-200 dark:ring-primary-800'
          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
      }`}
    >
      {/* Checkbox overlay */}
      {selectionMode && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleSelect(person.id);
          }}
          className="absolute top-2 left-2 z-10 rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label={isSelected ? 'Deselect person' : 'Select person'}
        >
          {isSelected ? (
            <CheckSquare className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          ) : (
            <Square className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          )}
        </button>
      )}

      <Link
        to={`/people/${person.id}`}
        className="block"
      >
        <div className={`flex items-start justify-between gap-2 sm:gap-3 ${selectionMode ? 'pl-7' : ''}`}>
          {/* Left: Avatar + Name + Contact */}
          <div className="flex items-start gap-2 sm:gap-3 min-w-0">
            {/* Avatar */}
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 text-xs sm:text-sm font-bold text-primary-700 dark:text-primary-300">
              {person.firstName.charAt(0)}{person.lastName.charAt(0)}
            </div>

            <div className="min-w-0">
              <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate">
                {name}
              </h3>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1 truncate max-w-[180px] sm:max-w-none">
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{person.email}</span>
                </span>
                {person.phone && (
                  <span className="hidden sm:flex items-center gap-1">
                    <Phone className="h-3 w-3 shrink-0" />
                    {person.phone}
                  </span>
                )}
                {person.location && (
                  <span className="hidden sm:flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {person.location}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: Score */}
          <div className="shrink-0">
            <ScoreChip score={person.bestScore} />
          </div>
        </div>

        {/* Bottom: Application count + date */}
        <div className={`mt-2 sm:mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 ${selectionMode ? 'pl-7' : ''}`}>
          <span className="flex items-center gap-1">
            <Briefcase className="h-3 w-3" />
            {person.applicationCount} app{person.applicationCount !== 1 ? 's' : ''}
          </span>
          {person.latestApplicationDate && (
            <span className="truncate ml-2">
              {formatDistanceToNow(new Date(person.latestApplicationDate), { addSuffix: true })}
            </span>
          )}
        </div>
      </Link>
    </div>
  );
}

export default function People() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const jobId = searchParams.get('jobId') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 20;

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const selectionMode = selectedIds.size > 0;

  const updateFilters = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!('page' in updates)) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => updateFilters({ search: value }),
    [updateFilters]
  );
  const handleStatusChange = useCallback(
    (value: string) => updateFilters({ status: value }),
    [updateFilters]
  );
  const handleJobChange = useCallback(
    (value: string) => updateFilters({ jobId: value }),
    [updateFilters]
  );
  const handlePageChange = useCallback(
    (newPage: number) => updateFilters({ page: String(newPage) }),
    [updateFilters]
  );

  const { data, isLoading, isFetching, error, refetch } = usePeople({
    page,
    limit,
    search: search || undefined,
    status: status || undefined,
    jobId: jobId ? parseInt(jobId, 10) : undefined,
  });

  // Fetch jobs for filter dropdown
  const { data: jobsData } = useJobs({ limit: 100, status: 'active' });
  const jobOptions: FilterOption[] = (jobsData?.data || []).map((j) => ({
    value: String(j.id),
    label: j.title,
  }));

  const hasActiveFilters = search || status || jobId;

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Bulk action mutations
  const batchArchiveMutation = useBatchArchivePeople();
  const batchReprocessMutation = useBatchReprocessPeople();

  // Current page person IDs for select-all
  const currentPageIds = useMemo(
    () => (data?.data || []).map((p) => p.id),
    [data?.data]
  );

  const allOnPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = currentPageIds.some((id) => selectedIds.has(id));

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (allOnPageSelected) {
      // Deselect all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      // Select all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [allOnPageSelected, currentPageIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBatchArchive = async () => {
    const ids = Array.from(selectedIds);
    if (!window.confirm(`Archive ${ids.length} selected people? Their applications will be set to stub status.`)) {
      return;
    }
    try {
      const result = await batchArchiveMutation.mutateAsync(ids);
      showToast(`Archived ${result.archivedCount} applications across ${ids.length} people`, 'success');
      setSelectedIds(new Set());
    } catch (err) {
      const error = err as Error;
      showToast(`Archive failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleBatchReprocess = async () => {
    const ids = Array.from(selectedIds);
    if (!window.confirm(`Reprocess ${ids.length} selected people? Their applications will be re-queued for AI review.`)) {
      return;
    }
    try {
      const result = await batchReprocessMutation.mutateAsync(ids);
      showToast(`Queued ${result.queuedCount} applications for reprocessing across ${ids.length} people`, 'success');
      setSelectedIds(new Set());
    } catch (err) {
      const error = err as Error;
      showToast(`Reprocess failed: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  if (error) {
    return (
      <div className="card dark:bg-gray-800 dark:border-gray-700 p-8 text-center">
        <Users className="mx-auto h-10 w-10 text-red-400 dark:text-red-500" />
        <p className="mt-3 text-red-600 dark:text-red-400 font-medium">Failed to load people</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        <button onClick={() => refetch()} className="btn btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">People</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {data?.pagination.total ?? 0} candidates
            {hasActiveFilters && ' (filtered)'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-secondary shrink-0"
          disabled={isLoading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Bulk Action Toolbar */}
      {selectionMode && (
        <div className="sticky top-0 z-20 card p-3 dark:bg-gray-800 dark:border-gray-700 border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/30">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-700 dark:text-primary-300">
              <CheckSquare className="h-4 w-4" />
              <span>{selectedIds.size} selected</span>
            </div>

            <div className="h-5 w-px bg-primary-200 dark:bg-primary-800" />

            <button
              onClick={handleBatchArchive}
              disabled={batchArchiveMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950 transition-colors disabled:opacity-50"
            >
              <Archive className="h-3.5 w-3.5" />
              {batchArchiveMutation.isPending ? 'Archiving...' : 'Archive Selected'}
            </button>

            <button
              onClick={handleBatchReprocess}
              disabled={batchReprocessMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors disabled:opacity-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {batchReprocessMutation.isPending ? 'Reprocessing...' : 'Reprocess Selected'}
            </button>

            <div className="flex-1" />

            <button
              onClick={handleClearSelection}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="card p-4 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-2 sm:gap-4">
          <div className="w-full sm:flex-1 sm:min-w-[200px]">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by name or email..."
              label="Search"
              isLoading={isFetching}
            />
          </div>
          <div className="w-full sm:w-auto">
            <FilterDropdown
              label="Status"
              options={statusOptions}
              value={status}
              onChange={handleStatusChange}
              allOptionLabel="All Statuses"
              size="md"
              fullWidth
              className="sm:w-auto"
            />
          </div>
          {jobOptions.length > 0 && (
            <div className="w-full sm:w-auto sm:max-w-xs">
              <FilterDropdown
                label="Job"
                options={jobOptions}
                value={jobId}
                onChange={handleJobChange}
                allOptionLabel="All Jobs"
                size="md"
                fullWidth
                className="sm:w-auto"
              />
            </div>
          )}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn btn-secondary text-sm w-full sm:w-auto min-h-[44px]">
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Select All / Grid Header */}
      {!isLoading && data && data.data.length > 0 && (
        <div className="flex items-center gap-3">
          <button
            onClick={handleSelectAll}
            className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={allOnPageSelected ? 'Deselect all on page' : 'Select all on page'}
          >
            {allOnPageSelected ? (
              <CheckSquare className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            ) : someOnPageSelected ? (
              <MinusSquare className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            ) : (
              <Square className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            )}
            <span>{allOnPageSelected ? 'Deselect all' : 'Select all'}</span>
          </button>
        </div>
      )}

      {/* People Grid */}
      {isLoading ? (
        <SkeletonCardGrid cards={9} columns={3} />
      ) : data?.data.length === 0 ? (
        <div className="card p-12 text-center dark:bg-gray-800 dark:border-gray-700">
          {hasActiveFilters ? (
            <>
              <Search className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-gray-500">No people match your search criteria</p>
              <button
                onClick={clearFilters}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700"
              >
                Clear filters
              </button>
            </>
          ) : (
            <>
              <Users className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-gray-500">No candidates yet</p>
              <p className="text-sm text-gray-400">
                Candidates will appear here once applications are synced from SmartRecruiters.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {data?.data.map((person) => (
            <PersonCard
              key={person.id}
              person={person}
              isSelected={selectedIds.has(person.id)}
              onToggleSelect={handleToggleSelect}
              selectionMode={selectionMode || false}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
            Showing{' '}
            <span className="font-medium">{(page - 1) * limit + 1}</span>
            {' '}-{' '}
            <span className="font-medium">{Math.min(page * limit, data.pagination.total)}</span>
            {' '}of <span className="font-medium">{data.pagination.total}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn btn-secondary disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => handlePageChange(Math.min(data.pagination.totalPages, page + 1))}
              disabled={page === data.pagination.totalPages}
              className="btn btn-secondary disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
